import React, { useState, useEffect, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { acquireAccessToken } from "../../utils/authToken";
import { useSearchParams } from "react-router-dom";
import { BatchEventEntity, BatchSearchRequest, BatchSearchResponse, BatchFacets } from "./types";
import { searchBatches } from "./batchDashboardService";
import { BatchSummaryBar } from "./components/BatchSummaryBar";
import { BatchDataGrid } from "./components/BatchDataGrid";
import { BatchDetailDrawer } from "./components/BatchDetailDrawer";
import { formatDateTime, formatPeriod, toPascalCase } from "./batchStatusHelper";

function exportBatchesToCsv(batches: BatchEventEntity[], useUtc: boolean): void {
  const headers = ["Batch ID", "Status", "Country", "Company Code", "Batch Type", "Document Count", "Period", "Created", "Sent", "Completed", "Retries", "Mode"];
  const escapeCell = (val: string): string => {
    if (val.includes(",") || val.includes('"') || val.includes("\n")) {
      return `"${val.replace(/"/g, '""')}"`;
    }
    return val;
  };
  const rows = batches.map((b) => [
    b.batchId,
    b.status,
    b.countryCode,
    b.companyCode,
    toPascalCase(b.configType),
    b.documentCount.toString(),
    formatPeriod(b.reportPeriodStart, b.reportPeriodEnd),
    formatDateTime(b.createdDateTime, useUtc),
    formatDateTime(b.sentDateTime, useUtc),
    formatDateTime(b.completedDateTime, useUtc),
    b.retryCount.toString(),
    b.mode,
  ].map(escapeCell).join(","));

  const csv = [headers.join(","), ...rows].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `batch-export-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const STATUS_COLORS: Record<string, string> = {
  completed: "#107C10",
  staging: "#C8C600",
  sent: "#0078D4",
  readytosend: "#CA5010",
  failed: "#D13438",
};

const CONFIG_TYPE_COLORS: Record<string, string> = {
  standard: "#0078D4",
  salesreport: "#00B7C3",
  paymentreport: "#8764B8",
};

const COUNTRY_COLORS: Record<string, string> = {
  TW: "#E3008C",
  FR: "#0078D4",
  BR: "#107C10",
};

const MODE_COLORS: Record<string, string> = {
  default: "#0078D4",
  uat: "#CA5010",
  test: "#8764B8",
};

export const BatchDashboard: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const accessTokenRef = useRef("");
  const account = accounts[0];
  const initialSearch = urlSearchParams.get("search") || "";
  const [searchText, setSearchText] = useState(initialSearch);
  const [searchCleared, setSearchCleared] = useState(false);
  const [searchResponse, setSearchResponse] = useState<BatchSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [useUtc, setUseUtc] = useState(true);
  const batchTriggerRef = React.useRef<HTMLElement | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<BatchEventEntity | null>(null);
  const [skip, setSkip] = useState(0);
  const pageSize = 25;

  console.log("[BatchDashboard] mount/render. urlSearchParams.search=", initialSearch, "current searchText=", searchText);

  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [configTypeFilters, setConfigTypeFilters] = useState<string[]>([]);
  const [countryCodeFilters, setCountryCodeFilters] = useState<string[]>([]);
  const [modeFilters, setModeFilters] = useState<string[]>([]);

  // Sync URL search param to state — handles full-page reloads where
  // useSearchParams may not have the value ready on the first render.
  useEffect(() => {
    const urlSearch = urlSearchParams.get("search") || "";
    if (urlSearch) {
      setSearchText((currentSearchText) => currentSearchText === urlSearch ? currentSearchText : urlSearch);
    }
  }, [urlSearchParams]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    if (accessTokenRef.current === "") {
      const token = await acquireAccessToken(instance, account);
      accessTokenRef.current = token;
      return token;
    }
    return accessTokenRef.current;
  }, [instance, account]);

  const performSearch = useCallback(async () => {
    // Use URL search param as fallback when React state hasn't synced yet
    // (happens on full-page reload via <a href> where useState initializer
    // may not capture the URL param on the first render).
    // Skip fallback when user explicitly cleared the search field.
    const effectiveSearchText = searchCleared ? "" : (searchText || urlSearchParams.get("search") || "");
    setLoading(true);
    try {
      const token = await getAccessToken();
      const request: BatchSearchRequest = { searchText: effectiveSearchText, statusFilters, configTypeFilters, countryCodeFilters, modeFilters, skip, pageSize };
      console.log("[BatchDashboard] performSearch request=", request);
      const response = await searchBatches(request, token);
      console.log("[BatchDashboard] performSearch response totalCount=", response.totalCount, "returnedBatchIds=", (response.batches || []).map((b) => b.batchId));
      setSearchResponse(response);
      // Sync the input field if URL param was used as fallback
      if (effectiveSearchText && !searchText) {
        setSearchText(effectiveSearchText);
      }
    } catch (err) {
      console.error("Search failed", err);
    } finally {
      setLoading(false);
    }
  }, [searchText, searchCleared, urlSearchParams, statusFilters, configTypeFilters, countryCodeFilters, modeFilters, skip, pageSize, getAccessToken]);

  useEffect(() => {
    performSearch();
  }, [performSearch]);

  const toggleFilter = (current: string[], setter: React.Dispatch<React.SetStateAction<string[]>>, value: string) => {
    setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    setSkip(0);
  };

  const hasActiveFilters =
    statusFilters.length > 0 ||
    configTypeFilters.length > 0 ||
    countryCodeFilters.length > 0 ||
    modeFilters.length > 0 ||
    searchText.trim() !== "";

  const resetFilters = () => {
    setStatusFilters([]);
    setConfigTypeFilters([]);
    setCountryCodeFilters([]);
    setModeFilters([]);
    setSearchText("");
    setSearchCleared(true);
    setSkip(0);
    setUrlSearchParams((prev) => { prev.delete("search"); return prev; }, { replace: true });
  };

  const facets: BatchFacets = searchResponse?.facets || { status: [], configType: [], countryCode: [], companyCode: [], documentType: [], mode: [] };
  const batches = searchResponse?.batches || [];
  const totalCount = searchResponse?.totalCount || 0;
  const currentPage = Math.floor(skip / pageSize) + 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div style={{ padding: '1.5rem', backgroundColor: "#FAF9F8", minHeight: "100vh" }}>
      <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', fontWeight: 600, color: '#323130' }}>Batch Dashboard</h2>
      {/* Search Bar */}
      <div style={{ display: "flex", gap: '0.75rem', marginBottom: '1.25rem', alignItems: "center", flexWrap: "wrap" }}>
        <label htmlFor="batch-search-input" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>Search batches</label>
        <input
          id="batch-search-input"
          type="search"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onInput={(e) => {
            const target = e.target as HTMLInputElement;
            if (target.value === "") {
              setSearchCleared(true);
              setUrlSearchParams((prev) => { prev.delete("search"); return prev; }, { replace: true });
              setSkip(0);
            } else {
              setSearchCleared(false);
            }
          }}
          onKeyDown={(e) => { if (e.key === "Enter") { setSearchCleared(false); setSkip(0); performSearch(); } }}
          placeholder="🔍 Search for batch IDs."
          aria-label="Search for batch IDs"
          title="Type a batch ID and press Enter to search"
          autoComplete="off"
          style={{ flex: 1, padding: "0.625rem 1rem", fontSize: '0.875rem', border: "1px solid #8A8886", borderRadius: '0.25rem', outline: "none", minWidth: 0 }}
        />
        <button onClick={() => { setSearchCleared(false); setSkip(0); performSearch(); }} aria-label="Search batches" title="Execute the batch search query" style={{ padding: "0.625rem 1.25rem", backgroundColor: "#0078D4", color: "#FFF", border: "none", borderRadius: '0.25rem', fontSize: '0.875rem', fontWeight: 600, cursor: "pointer", minHeight: '2rem', minWidth: '2rem' }}>
          Search
        </button>
      </div>

      {/* Summary Bars — horizontal row matching Document Dashboard layout */}
      <div style={{ backgroundColor: "#FFFFFF", padding: 20, borderRadius: 8, border: "1px solid #EDEBE9", marginBottom: 20 }}>
        <div style={{ display: "flex", flexDirection: "row", gap: 24, overflowX: "auto", width: "100%" }} tabIndex={0} role="region" aria-label="Batch summary charts">
          <div style={{ flex: 1, minWidth: 180 }}>
            <BatchSummaryBar title="Status" facets={facets.status} colorMap={STATUS_COLORS} selectedValues={statusFilters} onToggle={(v) => toggleFilter(statusFilters, setStatusFilters, v)} />
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <BatchSummaryBar title="Batch Type" facets={facets.configType} colorMap={CONFIG_TYPE_COLORS} selectedValues={configTypeFilters} onToggle={(v) => toggleFilter(configTypeFilters, setConfigTypeFilters, v)} />
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <BatchSummaryBar title="Country" facets={facets.countryCode} colorMap={COUNTRY_COLORS} selectedValues={countryCodeFilters} onToggle={(v) => toggleFilter(countryCodeFilters, setCountryCodeFilters, v)} />
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <BatchSummaryBar title="Mode" facets={facets.mode} colorMap={MODE_COLORS} selectedValues={modeFilters} onToggle={(v) => toggleFilter(modeFilters, setModeFilters, v)} />
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: '0.75rem', flexWrap: "wrap", gap: '0.5rem' }}>
        <div style={{ fontSize: '0.875rem', color: "#323130" }} role="status" aria-live="polite">{loading ? "Searching..." : `Found ${totalCount.toLocaleString()} Batches`}</div>
        <div style={{ display: "flex", gap: '0.75rem', alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={() => setUseUtc(!useUtc)} aria-label={useUtc ? "Time Zone: UTC" : "Time Zone: Local"} title={useUtc ? "Time Zone: UTC" : "Time Zone: Local"} style={{ padding: "0.375rem 0.75rem", fontSize: '0.75rem', border: "1px solid #EDEBE9", borderRadius: '0.25rem', backgroundColor: "#FFF", cursor: "pointer", minHeight: '2rem', minWidth: '2rem' }}>
          <span aria-hidden="true">⏰ </span>{useUtc ? "UTC" : "Local"}
        </button>
          <button onClick={resetFilters} disabled={!hasActiveFilters} aria-label="Reset filters" title="Clear all filters and search" style={{ padding: "0.375rem 0.75rem", fontSize: '0.75rem', border: "1px solid #EDEBE9", borderRadius: '0.25rem', backgroundColor: "#FFF", cursor: hasActiveFilters ? "pointer" : "not-allowed", minHeight: '2rem', minWidth: '2rem' }}><span aria-hidden="true">↺ </span>Reset</button>
          <button onClick={performSearch} aria-label="Refresh" title="Refresh batch search results" style={{ padding: "0.375rem 0.75rem", fontSize: '0.75rem', border: "1px solid #EDEBE9", borderRadius: '0.25rem', backgroundColor: "#FFF", cursor: "pointer", minHeight: '2rem', minWidth: '2rem' }}><span aria-hidden="true">🔄 </span>Refresh</button>
          <button onClick={() => exportBatchesToCsv(batches, useUtc)} disabled={batches.length === 0} aria-label="Export CSV" title="Export current batch results as CSV" style={{ padding: "0.375rem 0.75rem", fontSize: '0.75rem', border: "1px solid #EDEBE9", borderRadius: '0.25rem', backgroundColor: "#FFF", cursor: batches.length === 0 ? "not-allowed" : "pointer", minHeight: '2rem', minWidth: '2rem' }}><span aria-hidden="true">📥 </span>Export CSV</button>
        </div>
      </div>

      {/* Data Grid */}
      <div style={{ backgroundColor: "#FFFFFF", borderRadius: '0.5rem', border: "1px solid #EDEBE9" }}>
        <BatchDataGrid batches={batches} useUtc={useUtc} onBatchClick={(batch) => {
          batchTriggerRef.current = document.activeElement as HTMLElement;
          setSelectedBatch(batch);
        }} />
      </div>

      {/* Pagination */}
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: '0.5rem', marginTop: '0.75rem', fontSize: '0.8125rem', flexWrap: "wrap" }}>
        <button onClick={() => setSkip(Math.max(0, skip - pageSize))} disabled={currentPage <= 1} aria-disabled={currentPage <= 1} aria-label="Previous page" title="Go to the previous page of batch results" style={{ padding: "0.375rem 1rem", border: "1px solid #EDEBE9", borderRadius: '0.25rem', backgroundColor: "#FFF", cursor: currentPage <= 1 ? "not-allowed" : "pointer", fontSize: '0.8125rem', minHeight: '2rem' }}>Previous</button>
        <span style={{ color: "#605E5C" }} aria-live="polite" role="status">Page {currentPage} / {totalPages}</span>
        <button onClick={() => setSkip(skip + pageSize)} disabled={currentPage >= totalPages} aria-disabled={currentPage >= totalPages} aria-label="Next page" title="Go to the next page of batch results" style={{ padding: "0.375rem 1rem", border: "1px solid #EDEBE9", borderRadius: '0.25rem', backgroundColor: "#FFF", cursor: currentPage >= totalPages ? "not-allowed" : "pointer", fontSize: '0.8125rem', minHeight: '2rem' }}>Next</button>
      </div>

      {/* Detail Drawer */}
      <BatchDetailDrawer batch={selectedBatch} useUtc={useUtc} getAccessToken={getAccessToken} onClose={() => {
        setSelectedBatch(null);
        requestAnimationFrame(() => {
          batchTriggerRef.current?.focus();
        });
      }} />
    </div>
  );
};
